import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { handleError, ErrorSeverity, ErrorContext } from '@/utils/errorHandler';
import { SubscriptionService } from '@/services/subscriptionService';

const AuthCallback = () => {
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Process the OAuth callback or email verification
    const handleAuthCallback = async () => {
      const context: ErrorContext = {
        operation: 'handleAuthCallback',
        source: 'AuthCallback',
        additionalInfo: { 
          url: window.location.href,
          referrer: document.referrer 
        }
      };

      try {
        // Get the current session
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          throw error;
        }
        
        // If user is authenticated, redirect based on their role
        if (data.session) {
          const user = data.session.user;
          const userRole = user.user_metadata?.role;
          
          console.log('AuthCallback: User authenticated', { 
            userId: user.id, 
            email: user.email, 
            role: userRole,
            is_superuser: user.user_metadata?.is_superuser 
          });

          // Route based on user role
          if (user.user_metadata?.is_superuser || userRole === 'superadmin') {
            console.log('AuthCallback: Redirecting superadmin to /superadmin');
            navigate('/superadmin');
          } else if (userRole === 'bizadmin' || userRole === 'admin' || userRole === 'staff') {
            // Check if user is HiveShare-only and redirect accordingly
            const organizationId = user.user_metadata?.organization_id;
            if (organizationId) {
              try {
                const accessInfo = await SubscriptionService.getOrganizationAccess(organizationId);
                if (accessInfo.status === 'active-hiveshare') {
                  console.log('AuthCallback: Redirecting HiveShare-only user to /admin/hiveshare');
                  navigate('/admin/hiveshare');
                  return;
                }
              } catch (err) {
                console.error('AuthCallback: Error checking subscription status:', err);
                // Fall through to default routing if subscription check fails
              }
            }
            
            console.log('AuthCallback: Redirecting admin/staff to /dashboard');
            navigate('/dashboard');
          } else if (userRole === 'parent') {
            console.log('AuthCallback: Redirecting parent to /parent-portal');
            navigate('/parent-portal');
          } else {
            // Default fallback - could be a new user without role assigned yet
            console.log('AuthCallback: No specific role found, redirecting to /dashboard');
            navigate('/dashboard');
          }
        } else {
          // If no session, something went wrong with the auth process
          const noSessionError = new Error('No session found after authentication');
          handleError(
            noSessionError,
            'Authentication process incomplete',
            ErrorSeverity.WARNING,
            context,
            false
          );
          navigate('/auth');
        }
      } catch (err: any) {
        console.error('Auth callback error:', err);
        
        const formattedError = handleError(
          err,
          'Authentication failed',
          ErrorSeverity.ERROR,
          context,
          false
        );
        
        setError(formattedError.message || 'Authentication failed');
        
        // Redirect back to login after a short delay
        setTimeout(() => {
          navigate('/auth');
        }, 3000);
      }
    };

    handleAuthCallback();
  }, [navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-md">
        {error ? (
          <div className="text-center">
            <h1 className="text-xl font-semibold text-red-600">Authentication Error</h1>
            <p className="mt-2 text-gray-600">{error}</p>
            <p className="mt-4">Redirecting back to login...</p>
          </div>
        ) : (
          <div className="text-center">
            <h1 className="text-xl font-semibold text-gray-900">Authentication Successful</h1>
            <p className="mt-2 text-gray-600">Processing your authentication...</p>
            <div className="mt-4 flex justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-primary"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
